import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgbCollapseModule, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { PAGE } from '../../app.routes';
import { APP_CONFIGURATION_TOKEN, DateOnly, ToastService } from '../../core';
import { MotMessagesData, RequestFrom } from '../../models';
import { PatientSettingsService } from '../../services';
import { BaseComponent, ConfirmationComponent, PublishedPlansProgressComponent } from '../../shared';
import { ContactInfoComponent } from '../contact-info';
import { MotivationalMessagesComponent } from '../motivational-messages';
import { PinSiteCareComponent } from '../pin-site-care';


/**
 * Component for patient settings page.
 */
@Component({
	selector: 'tsp-patient-settings',
	imports: [
		CommonModule,
		NgbCollapseModule,
		ContactInfoComponent,
		PinSiteCareComponent,
		MotivationalMessagesComponent,
		PublishedPlansProgressComponent
	],
	templateUrl: './patient-settings.component.html',
	changeDetection: ChangeDetectionStrategy.Eager
})
export class PatientSettingsComponent extends BaseComponent implements OnInit, OnDestroy {

	private readonly router = inject(Router);
	private readonly modalSrv = inject(NgbModal);
	private readonly toastSrv = inject(ToastService);
	private readonly configs = inject(APP_CONFIGURATION_TOKEN);
	private readonly patStgSrv = inject(PatientSettingsService);

	patientGuid = input<string>();
	reqFrom = input<RequestFrom>();

	patient = computed(() => this.patStgSrv.patient());

	contactInfo = computed(() => this.patStgSrv.patSettings()?.email ?? null);
	secondContactInfo = computed(() => this.patStgSrv.patSettings()?.secondEmail ?? null);
	isContactInfoValid = this.patStgSrv.isContactInfoValid;

	isPinSiteCareEnabled = computed(() => this.patStgSrv.patSettings()?.pinSiteCareEnable ?? false);
	firstDay = computed(() => this.patStgSrv.patSettings()?.firstDay);
	frequency = computed(() => this.patStgSrv.patSettings()?.frequency);
	isPinSiteCareValid = this.patStgSrv.isPinSiteCareValid;

	motMessagesData = computed(() => {
		const patStgs = this.patStgSrv.patSettings();
		if (!patStgs) return undefined;
		return {
			userId: this.patStgSrv.patient()!.userGuid,
			enabled: patStgs.mmEnable,
			customSet: patStgs.customSet,
			enableSet: patStgs.enabledSet
		} as MotMessagesData
	});
	isMotMessagesValid = this.patStgSrv.isMotMessagesValid;

	// pinSiteCarePdf = signal<PatientFileBase | undefined>(undefined);
	// physiotherapyPdf = signal<PatientFileBase | undefined>(undefined);

	publishedPlans = computed(() => this.patStgSrv.publishedPlans() ?? undefined);

	isVisible = computed(() => !!this.patStgSrv.patSettings());
	isReadonly = computed(() => this.patStgSrv.isReadonly());
	isBtnVisible = computed(() => this.isVisible() && !this.isReadonly());
	isUpdateEnabled = computed(() => this.patStgSrv.isPatientValid());
	isRevokeEnabled = computed(() => !!this.publishedPlans()?.length);

	// pdfPinSiteCareOpened = false;
	msgOpened = false;
	// pdfPhysioOpened = false;
	progressOpened = false;

	private _revokeSuccessTimeoutId: NodeJS.Timeout | undefined;
	private _updateSuccessTimeoutId: NodeJS.Timeout | undefined;



	ngOnInit(): void {
		if (this.patientGuid()) {
			this.patStgSrv.init(this.patientGuid()!).subscribe(res => {
				if (!res) this.toastSrv.showError(this.labels().PATIENT_SETTINGS_COMPONENT_LOAD_ERROR);
				// this.pinSiteCarePdf.set(this.patStgSrv.patSettings()?.pinSiteCarefile);
				// this.physiotherapyPdf.set(this.patStgSrv.patSettings()?.physiotherapyfile);
			});
		} else {
			throw new Error('Patient Guid is required!');
		}
	}

	/**
	 * Update email address.
	 */
	updateEmail(email: string | null): void {
		if (this.isContactInfoValid() && email) {
			this.patStgSrv.patSettings()!.email = email;
		}
	}

	/**
	 * Update second email address.
	 */
	updateSecondEmail(email: string | null): void {
		if (this.isContactInfoValid()) {
			this.patStgSrv.patSettings()!.secondEmail = email?.length ? email : null;
		}
	}

	/**
	 * Enable Pin Site Care.
	 */
	enablePinSiteCare(val: boolean): void {
		this.patStgSrv.patSettings()!.pinSiteCareEnable = val;
	}

	/**
	 * Update First Day.
	 */
	updateFirstDay(date?: Date | null): void {
		this.patStgSrv.patSettings()!.firstDay = date ? new DateOnly(date) : undefined;
	}

	/**
	 * Update Frequency.
	 */
	updateFrequency(freq: boolean[] | undefined): void {
		this.patStgSrv.patSettings()!.frequency = freq;
	}

	/**
	 * Enable Motivational Messages.
	 */
	enableMotMessages(val: boolean): void {
		this.patStgSrv.patSettings()!.mmEnable = val;
	}

	/**
	 * Update custom set.
	 */
	updateCustomSet(set: string | null): void {
		this.patStgSrv.patSettings()!.customSet = set ?? undefined;
	}

	/**
	 * Update enable set.
	 */
	updateEnableSet(set: number[] | undefined): void {
		this.patStgSrv.patSettings()!.enabledSet = set;
	}

	/**
	 * Update Pin Site Care file.
	 */
	// updatePinSiteCareFile(file: File | null): void {
	// 	file ? this._updateFile(file, FileCategory.PINSITECARE) : this._openDeleteFileModal(FileCategory.PINSITECARE);
	// }

	/**
	 * Update Physiotherapy file.
	 */
	// updatePhysiotherapyFile(file: File | null): void {
	// 	file ? this._updateFile(file, FileCategory.PHYSIOTHERAPY) : this._openDeleteFileModal(FileCategory.PHYSIOTHERAPY);
	// }

	/**
	 * Back to previous page without save.
	 */
	cancel(): void {
		this._backToPreviousPage();
	}

	/**
	 * Open revoke all confirmation modal.
	 */
	openRevokeAllModal(): void {
		if (this.isRevokeEnabled()) {
			const modalRef: NgbModalRef = this.modalSrv.open(ConfirmationComponent, { centered: true, backdrop: 'static' });
			(modalRef.componentInstance as ConfirmationComponent).config = {
				title: this.labels().PATIENT_SETTINGS_COMPONENT_REVOKEALL_MODAL_TITLE,
				message: this.labels().PATIENT_SETTINGS_COMPONENT_REVOKEALL_MODAL_MSG,
				confirmBtn: this.labels().PATIENT_SETTINGS_COMPONENT_REVOKEALL_MODAL_CONFIRM,
				cancelBtn: this.labels().PATIENT_SETTINGS_COMPONENT_REVOKEALL_MODAL_CANCEL
			};
			modalRef.result.then(() => this._revokeAll()).catch(() => false);
		}
	}

	/**
	 * Revoke all treatment plans.
	 */
	private _revokeAll(): void {
		if (this.isRevokeEnabled()) {
			this.patStgSrv.revokeAll().subscribe({
				next: () => {
					this.toastSrv.showSuccess(this.labels().PATIENT_SETTINGS_COMPONENT_REVOKE_SUCCESS);
					this._revokeSuccessTimeoutId = setTimeout(() => this._backToPreviousPage(), 3000);
				},
				error: () => this.toastSrv.showError(this.labels().PATIENT_SETTINGS_COMPONENT_REVOKE_ERROR)
			})
		}
	}

	/**
	 * Update patient settings data.
	 */
	update(): void {
		if (this.isUpdateEnabled()) {
			this.patStgSrv.update().subscribe({
				next: () => {
					this.toastSrv.showSuccess(this.labels().PATIENT_SETTINGS_COMPONENT_UPDATE_SUCCESS);
					this._updateSuccessTimeoutId = setTimeout(() => this._backToPreviousPage(), 3000);
				},
				error: () => this.toastSrv.showError(this.labels().PATIENT_SETTINGS_COMPONENT_UPDATE_ERROR)
			});
		}
	}

	// private _updateFile(file: File, category: FileCategory): void {
	// 	this.patStgSrv.updateFile(file, category).subscribe({
	// 		next: () => {
	// 			this.toastSrv.showSuccess(this.labels().PATIENT_SETTINGS_COMPONENT_UPDATEFILE_SUCCESS);
	// 			this._updateFilename(file.name, category);
	// 		},
	// 		error: () => this.toastSrv.showError(this.labels().PATIENT_SETTINGS_COMPONENT_UPDATEFILE_ERROR)
	// 	});
	// }

	// private _updateFilename(name: string | undefined, category: FileCategory): void {
	// 	if (category === FileCategory.PINSITECARE) {
	// 		this.pinSiteCarePdf.update(f => name ? ({ ...f, name: name } as PatientFileBase) : undefined);
	// 	} else {
	// 		this.physiotherapyPdf.update(f => name ? ({ ...f, name: name } as PatientFileBase) : undefined);
	// 	}
	// }

	// private _openDeleteFileModal(category: FileCategory): void {
	// 	const modalRef: NgbModalRef = this.modalSrv.open(ConfirmationComponent, { centered: true, backdrop: 'static' });
	// 	(modalRef.componentInstance as ConfirmationComponent).config = {
	// 		title: this.labels().PATIENT_SETTINGS_COMPONENT_DELETEMODAL_TITLE,
	// 		message: this.labels().PATIENT_SETTINGS_COMPONENT_DELETEMODAL_MSG,
	// 		confirmBtn: this.labels().PATIENT_SETTINGS_COMPONENT_DELETEMODAL_CONFIRM
	// 	};
	// 	modalRef.result.then(() => this._deleteFile(category)).catch(() => false);
	// }

	// private _deleteFile(category: FileCategory): void {
	// 	this.patStgSrv.deleteFile(category).subscribe({
	// 		next: () => {
	// 			this.toastSrv.showSuccess(this.labels().PATIENT_SETTINGS_COMPONENT_DELETEFILE_SUCCESS);
	// 			this._updateFilename(undefined, category);
	// 		},
	// 		error: () => this.toastSrv.showError(this.labels().PATIENT_SETTINGS_COMPONENT_DELETEFILE_ERROR)
	// 	});
	// }

	private _backToPreviousPage(): void {
		if (this.reqFrom() === RequestFrom.PATIENT) {
			window.location.href = this.configs.patListSite;
		} else {
			this.router.navigateByUrl(PAGE.PATIENT_LIST);
		}
	}


	ngOnDestroy(): void {
		this.patStgSrv.reset();
		clearTimeout(this._revokeSuccessTimeoutId);
		clearTimeout(this._updateSuccessTimeoutId);
	}

}
